import {
  HttpRequest,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpErrorResponse,
  HttpContextToken
} from '@angular/common/http';
import { Observable, catchError, throwError, timer, retry } from 'rxjs';
import { inject } from '@angular/core';
import { LogService } from '../services/log.service';
import { ServerErrorDialogService } from '../services/server-error-dialog.service';
import { UserInteractionService, UserInteractionSnapshot } from '../services/user-interaction.service';

/**
 * Estados HTTP que el llamador trata como un resultado de negocio y no como un fallo: la respuesta trae un
 * cuerpo `{ ok: false, failureReason }` (p. ej. el 409 `FORM_IS_OPEN` de activar un formulario) que la pantalla
 * necesita leer. Una petición que lo declara recibe de vuelta el `HttpErrorResponse` original: sin reintentos, sin
 * aviso al usuario y sin transformarlo en `ApiError`, que perdería el cuerpo. Un error de esos estados sin ese
 * cuerpo (ModelState, ProblemDetails) sigue el camino normal.
 */
export const HANDLED_RESULT_STATUSES = new HttpContextToken<readonly number[]>(() => []);

function isHandledResult(req: HttpRequest<unknown>, error: HttpErrorResponse): boolean {
  if (!req.context.get(HANDLED_RESULT_STATUSES).includes(error.status)) {
    return false;
  }
  const body = error.error as { ok?: unknown } | null;
  return !!body && typeof body === 'object' && body.ok === false;
}

/**
 * Peticiones que llaman a un modelo de IA de pago y no son idempotentes: cada intento es otra llamada
 * facturada y un fallo del proveedor (400, salida inutilizable) no es transitorio, así que no se reintentan.
 * El 502 `{ message }` del asistente de informes (JSON del modelo inutilizable) no trae código de IA.
 */
const AI_MODEL_POST_URLS = [/\/jrxml-designer\/assistant(?:\?|$)/];

/**
 * Un fallo de la capa de IA no se reintenta: el cuerpo es un ProblemDetails con `code: "ai_..."` (lo escribe
 * `ApiExceptionHandlingMiddleware.WriteAiProblemAsync`) o la petición es un POST que llama al modelo.
 */
function isAiFailure(req: HttpRequest<unknown>, error: HttpErrorResponse): boolean {
  const body = error.error as { code?: unknown } | null;
  if (!!body && typeof body === 'object' && typeof body.code === 'string' && body.code.startsWith('ai_')) {
    return true;
  }
  return req.method === 'POST' && AI_MODEL_POST_URLS.some((pattern) => pattern.test(req.url));
}

/**
 * Error response structure with user-friendly message
 */
export interface ApiError {
  message: string;
  statusCode: number;
  details?: string;
  /** Código de negocio del cuerpo (p. ej. `ACCOUNT_PENDING_ACTIVATION`), si el servidor lo manda. */
  code?: string;
  timestamp: Date;
  url?: string;
}

/**
 * Configuration for error handling interceptor
 */
interface ErrorInterceptorConfig {
  maxRetries?: number;
  retryDelayMs?: number;
  skipRetryFor?: number[]; // HTTP status codes to skip retry
  showUserFriendlyErrors?: boolean;
}

/**
 * Default configuration
 */
const DEFAULT_CONFIG: ErrorInterceptorConfig = {
  maxRetries: 2,
  retryDelayMs: 1000,
  skipRetryFor: [400, 401, 403, 404, 422], // Don't retry client errors
  showUserFriendlyErrors: true
};

/**
 * HTTP interceptor for error handling and retry logic.
 *
 * Features:
 * - Automatic retry for failed requests (configurable)
 * - User-friendly error messages
 * - Comprehensive error logging
 * - Status code specific handling
 *
 * Usage:
 * ```typescript
 * // In app.config.ts (Angular 17+)
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     provideHttpClient(withInterceptors([errorInterceptor, authInterceptor, loadingInterceptor]))
 *   ]
 * };
 * ```
 */
const errorInterceptorInternal = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  config: ErrorInterceptorConfig = DEFAULT_CONFIG
): Observable<HttpEvent<unknown>> => {
  const log = inject(LogService);
  const serverErrorDialog = inject(ServerErrorDialogService);
  const userInteraction = inject(UserInteractionService);
  const finalConfig = { ...DEFAULT_CONFIG, ...config };

  return next(req).pipe(
    retry({
      count: finalConfig.maxRetries ?? 2,
      delay: (error, retryCount) => {
        // Don't retry on client errors or if configured to skip; a handled business result or an AI failure is never retried
        if (
          isHandledResult(req, error) ||
          isAiFailure(req, error) ||
          shouldSkipRetry(error, finalConfig.skipRetryFor ?? [])
        ) {
          throw error;
        }

        // Calculate delay with exponential backoff
        const delay = (finalConfig.retryDelayMs ?? 1000) * Math.pow(2, retryCount - 1);
        log.warn(`Retrying request (${retryCount}/${finalConfig.maxRetries})`, {
          url: req.url,
          method: req.method,
          delay,
          error: error.message
        });

        return timer(delay);
      }
    }),
    catchError((error: HttpErrorResponse) => {
      if (isHandledResult(req, error)) {
        return throwError(() => error);
      }

      const apiError = transformError(error, req.url);
      logError(log, apiError, req);

      if (error.status >= 500) {
        serverErrorDialog.show(
          buildServerErrorDialogState(
            error,
            apiError,
            req.method,
            userInteraction.lastInteraction(),
            sanitizeHeaders(req.headers)
          )
        );
      } else if (finalConfig.showUserFriendlyErrors) {
        // Could integrate with a toast/notification service here
        showErrorMessage(apiError);
      }

      return throwError(() => apiError);
    })
  );
};

export const errorInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) =>
  errorInterceptorInternal(req, next, DEFAULT_CONFIG);

/**
 * Check if error should skip retry based on status code
 */
function shouldSkipRetry(error: HttpErrorResponse, skipStatusCodes: number[]): boolean {
  if (error.status && skipStatusCodes.includes(error.status)) {
    return true;
  }

  // Skip retry for network errors (no status code)
  if (!error.status) {
    return true;
  }

  return false;
}

/**
 * Transform HttpErrorResponse to ApiError with user-friendly message
 */
function transformError(error: HttpErrorResponse, url: string): ApiError {
  const apiError: ApiError = {
    message: getUserFriendlyMessage(error),
    statusCode: error.status || 0,
    timestamp: new Date(),
    url
  };

  // Add details from error response if available
  if (error.error) {
    if (typeof error.error === 'object' && typeof error.error.code === 'string') {
      apiError.code = error.error.code;
    }
    if (typeof error.error === 'string') {
      apiError.details = error.error;
    } else if (error.error.message) {
      apiError.details = error.error.message;
    } else if (error.error.detail) {
      // ASP.NET ProblemDetails payloads expose the message under `detail`.
      apiError.details = error.error.detail;
    } else if (error.error.error) {
      apiError.details = error.error.error;
    }
  }

  return apiError;
}

/**
 * Generate user-friendly error message based on status code
 */
function getUserFriendlyMessage(error: HttpErrorResponse): string {
  if (error.status === 0) {
    return 'No se puede conectar con el servidor. Verifica tu conexión a internet.';
  }

  switch (error.status) {
    case 400:
      return 'Solicitud incorrecta. Por favor, verifica los datos enviados.';
    case 401:
      return 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.';
    case 403:
      return 'No tienes permisos para realizar esta acción.';
    case 404:
      return 'El recurso solicitado no existe.';
    case 409:
      return 'Hay un conflicto con los datos actuales. Por favor, recarga y reintenta.';
    case 422:
      return 'Los datos enviados no son válidos.';
    case 429:
      return 'Has realizado demasiadas solicitudes. Por favor, espera unos minutos.';
    case 500:
      return 'Ha ocurrido un error en el servidor. Por favor, reintenta más tarde.';
    case 502:
    case 503:
      return 'El servidor no está disponible. Por favor, reintenta en unos momentos.';
    case 504:
      return 'La solicitud está tardando demasiado. Por favor, reintenta.';
    default:
      return 'Ha ocurrido un error inesperado. Por favor, reintenta.';
  }
}

/**
 * Log error with appropriate level
 */
function logError(log: LogService, apiError: ApiError, req: HttpRequest<unknown>): void {
  const context = {
    url: req.url,
    method: req.method,
    statusCode: apiError.statusCode,
    timestamp: apiError.timestamp.toISOString()
  };

  // Log as error for server errors, warn for client errors
  if (apiError.statusCode >= 500) {
    log.error(`HTTP Error ${apiError.statusCode}: ${apiError.message}`, context, apiError.details);
  } else if (apiError.statusCode >= 400) {
    log.warn(`HTTP Error ${apiError.statusCode}: ${apiError.message}`, context);
  } else {
    log.error(`Network Error: ${apiError.message}`, context);
  }
}

/**
 * Show error message to user (placeholder for toast/notification integration)
 */
function showErrorMessage(apiError: ApiError): void {
  // For now, just log to console.
  console.error(`[API Error] ${apiError.message}`, apiError);
}

/**
 * Export a configured version of the interceptor for easy use
 */
export const errorInterceptorWithDefaults = (req: HttpRequest<unknown>, next: HttpHandlerFn) =>
  errorInterceptorInternal(req, next, DEFAULT_CONFIG);

/**
 * Create a custom error interceptor with specific configuration
 */
export function createErrorInterceptor(config: ErrorInterceptorConfig): HttpInterceptorFn {
  return (req: HttpRequest<unknown>, next: HttpHandlerFn) =>
    errorInterceptorInternal(req, next, config);
}

function buildServerErrorDialogState(
  error: HttpErrorResponse,
  apiError: ApiError,
  method: string,
  lastInteraction: UserInteractionSnapshot | null,
  requestHeaders: Record<string, string>
) {
  const body = error.error as Record<string, any> | null | undefined;
  const message = body?.detail || apiError.details || apiError.message;

  return {
    message,
    statusCode: apiError.statusCode,
    url: apiError.url,
    method,
    timestamp: apiError.timestamp.toISOString(),
    exceptionType: body?.exceptionType,
    stackTrace: body?.stackTrace,
    lastInteraction,
    requestHeaders
  };
}

function sanitizeHeaders(headers: HttpRequest<unknown>['headers']): Record<string, string> {
  const sensitiveHeaders = new Set(['authorization', 'cookie', 'set-cookie', 'x-api-key']);
  const result: Record<string, string> = {};

  for (const key of headers.keys()) {
    const normalized = key.toLowerCase();
    if (sensitiveHeaders.has(normalized)) {
      result[key] = '[REDACTED]';
      continue;
    }

    const values = headers.getAll(key);
    if (!values || values.length === 0) {
      result[key] = '';
    } else if (values.length === 1) {
      result[key] = values[0];
    } else {
      result[key] = values.join(', ');
    }
  }

  return result;
}
